"use client";

import { useEffect, useRef, useState } from "react";

const interestedIn = ["Canada", "Australia", "United Kingdom", "Germany", "Ireland", "New Zealand", "Not Sure Yet"];
const stages = ["Just Exploring", "Researching Universities", "Ready to Apply", "Applied, Need Visa Help"];
const lookingFor = ["Bachelor\'s Program", "Master\'s Program", "PhD Programs", "Others"];

const faqs = [
  {
    q: "How much does studying abroad actually cost? Can my family afford it?",
    a: "This depends entirely on the country, university, and program you choose — and there's far more range than most people realize. Germany's public universities charge near-zero tuition. Canada and Australia have scholarships that can significantly offset costs. Our first job is helping you find the pathway that's realistic for your financial situation — not selling you the most expensive option."
  },
  {
    q: "What if my academic profile isn't strong enough?",
    a: "There\'s a university and program for every profile level — the key is knowing which ones are genuinely right for you. We\'ve helped students with average grades find excellent universities that matched their strengths. We\'ve also helped high-achievers avoid overshooting into programs they weren\'t ready for. Honest assessment. Smart targeting. That\'s the formula."
  },
  {
    q: "Can you guarantee a visa approval?",
    a: "No ethical consultancy can guarantee a visa — it's a government decision. What we can do — and do consistently — is prepare your application so thoroughly that rejection becomes unlikely. Our 98% visa success rate is the result of that preparation. We know what works. We know what doesn't. And we apply that knowledge to your file."
  },
  {
    q: "How is Global Varsity different from other consultancies?",
    a: "Most consultancies earn money from universities and institutions — which creates a conflict of interest. We're transparent about this. Our primary loyalty is to you, the student. We'll recommend the option that's best for your future, even if it's not the most lucrative for us. That's a rare position in this industry, and it's the reason our students trust us and refer their friends."
  },
  {
    q: "What if my visa gets rejected even with your help?",
    a: "It's rare — but it happens. When it does, we don't disappear. We analyze the rejection, identify the reason, and work with you on a reapplication strategy. Several of our most successful students had initial rejections that we turned around."
  },
  {
    q: "How early should I start the process?",
    a: "Ideally, 12–18 months before your intended start date. This gives you time to strengthen your profile, apply to multiple universities, await decisions, and prepare your visa without rushing. That said, we've also helped students with 6-month timelines succeed. The sooner you start, the better your options."
  },
  {
    q: "I\'ve never been abroad. Is this process really manageable?",
    a: "Yes — and that's exactly why we exist. Our job is to make something that feels impossible feel straightforward. By the time you board your flight, you'll have done it all: the applications, the visa, the accommodation, the logistics. We guide you through every single step."
  },
  {
    q: "Do you only help students, or do you help families too?",
    a: "Both. We regularly work with parents who want to understand the process, the costs, and the safety of different destinations. Your family's peace of mind matters as much as your ambition — and we're here for both."
  }
];

export default function ContactSection() {
  const ref = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLElement>(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    destination: "",
    stage: "",
    lookingFor: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );
    ref.current?.querySelectorAll(".reveal, .reveal-scale").forEach((el) => observer.observe(el));
    contactRef.current?.querySelectorAll(".reveal, .reveal-scale").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submitted:", form);
    setSubmitted(true);
  };

  return (
    <>
      {/* FAQ Section */}
      <section id="faq" ref={ref} className="py-28 px-6 lg:px-10 dot-pattern">
        <div className="max-w-site mx-auto">
          <div className="text-center mb-14">
            <div className="reveal flex justify-center mb-4">
              <span className="section-label">Honest Answers</span>
            </div>
            <h2
              className="font-serif reveal delay-1 mb-2"
              style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
              Questions You're Probably{" "}
              <span className="gradient-text italic">Thinking.</span>
            </h2>
            <p className="text-ink-muted text-lg max-w-xl mx-auto reveal delay-2" style={{ fontWeight: 400 }}>
              Answers You Actually Need.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`rounded-[14px] border bg-white ${openFaq === i ? "border-crimson/30 shadow-crimson-sm" : "border-crimson-100 hover:border-crimson/20"}`}
              >
                <button
                  className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-ink text-sm leading-snug">{faq.q}</span>
                  <span className={`text-crimson flex-shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </span>
                </button>
                <div
                  style={{
                    maxHeight: openFaq === i ? "400px" : "0px",
                    overflow: "hidden",
                    transition: "max-height 0.35s ease",
                  }}
                >
                  <div className="px-6 pb-5">
                    <p className="text-sm text-ink-muted leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" ref={contactRef} className="py-28 px-6 lg:px-10 bg-white">
        <div className="max-w-site mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left: Info */}
            <div>
              <div className="reveal mb-4">
                <span className="section-label">Get In Touch</span>
              </div>
              <h2
                className="font-serif reveal delay-1 mb-4"
                style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.1 }}
              >
                Book Your{" "}
                <span className="gradient-text italic">Free Strategy Session</span>
              </h2>
              <p className="text-ink-muted leading-relaxed mb-10 reveal delay-2">
                A free, 60-minute strategy session with one of our senior counsellors. No commitment. No sales pressure. Just a clear plan for your future.
              </p>

              {/* What we'll tell you */}
              <div className="mb-8 reveal delay-2 p-5 rounded-[16px] bg-parchment border border-crimson-100">
                <p className="font-semibold text-ink text-sm mb-3">We'll tell you honestly:</p>
                <div className="space-y-2">
                  {[
                    "Which countries best fit your profile and budget",
                    "What your realistic chances look like",
                    "What you need to do next — and when"
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-2">
                      <span className="text-crimson font-bold text-sm mt-0.5">→</span>
                      <p className="text-sm text-ink-muted">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact details */}
              <div className="space-y-5 reveal delay-3">
                {[
                  {
                    icon: "📞",
                    label: "Call / WhatsApp",
                    value: "+91 93525 89050 / +91 93583 65675",
                  },
                  {
                    icon: "📧",
                    label: "Email",
                    value: "Admissions@Globalvarsity.org",
                  },
                  {
                    icon: "🕐",
                    label: "Office Hours",
                    value: "Mon–Sat, 9:30 AM – 7:00 PM IST",
                  },
                ].map((item) => (
                  <div key={item.label} className="flex gap-4 p-4 rounded-[14px] bg-parchment border border-crimson-50 hover:border-crimson/20 transition-all duration-300 group">
                    <div className="w-10 h-10 rounded-[10px] bg-crimson/8 flex items-center justify-center text-xl flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-ink-muted uppercase tracking-wider mb-0.5">{item.label}</p>
                      <p className="text-sm font-medium text-ink">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Trust indicators */}
              <div className="mt-10 reveal delay-4 p-5 rounded-[16px] crimson-card text-white">
                <p className="font-serif font-semibold text-lg mb-3">Join 500+ students who chose Global Varsity — and never looked back.</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    "✓ Free consultation",
                    "✓ No spam",
                    "✓ Response within 24 hours",
                    "✓ 98% visa success rate",
                  ].map((item) => (
                    <p key={item} className="text-sm text-white/90">{item}</p>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="reveal-scale delay-2">
              {submitted ? (
                <div className="rounded-card border border-crimson-100 bg-parchment p-10 text-center">
                  <div className="w-16 h-16 rounded-full bg-crimson/10 flex items-center justify-center text-3xl mx-auto mb-4">
                    ✅
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-ink mb-3">
                    You just took the most important step.
                  </h3>
                  <p className="text-ink-muted leading-relaxed">
                    We've received your details and one of our senior counsellors will reach out within 24 hours. While you wait — here's what other students said about their first session with us.
                  </p>
                  <p className="text-sm text-crimson font-semibold mt-4">
                    Expected response: within 24 hours
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="rounded-card border border-crimson-100 bg-parchment p-8 shadow-soft space-y-5"
                >
                  <h3 className="font-serif text-xl font-bold text-ink mb-2">
                    Book Your Free Strategy Session
                  </h3>
                  <p className="text-sm text-ink-muted mb-4">
                    Fill in your details and we'll get back to you within 24 hours.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider mb-1.5">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Your Full Name"
                        className="form-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider mb-1.5">
                        Phone / WhatsApp *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        required
                        placeholder="Best Number to Reach You"
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider mb-1.5">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="We Don't Spam. Ever."
                      className="form-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider mb-1.5">
                        Where Are You Thinking?
                      </label>
                      <select
                        name="destination"
                        value={form.destination}
                        onChange={handleChange}
                        className="form-input"
                      >
                        <option value="">Select destination</option>
                        {interestedIn.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider mb-1.5">
                        Current Stage
                      </label>
                      <select
                        name="stage"
                        value={form.stage}
                        onChange={handleChange}
                        className="form-input"
                      >
                        <option value="">Select stage</option>
                        {stages.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider mb-1.5">
                      Looking For
                    </label>
                    <select
                      name="lookingFor"
                      value={form.lookingFor}
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">Select program type</option>
                      {lookingFor.map((l) => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider mb-1.5">
                      Tell Us a Little About Your Plans
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Your current education, target course, budget range, any specific questions..."
                      className="form-input resize-none"
                    />
                  </div>

                  <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2 text-base py-4">
                    Send My Details — I'm Ready to Start
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M3.75 9h10.5M10 5.25l4.5 3.75-4.5 3.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>

                  <div className="flex items-center justify-center gap-6 text-xs text-ink-muted">
                    <span>✓ Free consultation</span>
                    <span>✓ No spam</span>
                    <span>✓ Response within 24 hours</span>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-6 lg:px-10 crimson-card text-white">
        <div className="max-w-site mx-auto text-center">
          <h2 className="font-serif text-4xl lg:text-5xl font-bold mb-4" style={{ letterSpacing: "-0.02em" }}>
            Your Future Is Waiting.
          </h2>
          <p className="text-xl text-white/80 mb-4">The Only Question Is: Are You Ready to Claim It?</p>
          <p className="text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
            500+ students decided they were ready. They booked a call. They got a plan. They got on a plane. Now they're building careers at companies and institutions that, one year ago, felt like distant dreams. The next success story could be yours.
          </p>
          <a href="#contact" className="inline-flex items-center gap-2 bg-white text-crimson font-bold px-8 py-4 rounded-full hover:bg-parchment transition-all duration-300 text-base shadow-lg hover:shadow-xl hover:-translate-y-0.5">
            Book Your Free 60-Minute Strategy Session
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M3.75 9h10.5M10 5.25l4.5 3.75-4.5 3.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
          <p className="text-white/50 text-sm mt-4">No fees. No pressure. No vague promises. Just clarity.</p>
        </div>
      </section>
    </>
  );
}